import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import logo from "../assets/logo/mp_logo_white.png";
import {
  Flex,
  Image,
  Link,
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { HamburgerIcon } from "@chakra-ui/icons";
import { HomeContent, HomeLang } from "../data/homeContent";

type HeaderProps = {
  nav: HomeContent["nav"];
  lang: HomeLang;
  onLangChange: (lang: HomeLang) => void;
};

const Header = ({ nav, lang, onLangChange }: HeaderProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleScroll = (id: string) => {
    onClose();

    setTimeout(() => {
      const section = document.getElementById(id);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      } else {
        console.error(`Section with id "${id}" not found!`);
      }
    }, 300);
  };

  const langToggle = (size: "xs" | "sm") => (
    <ButtonGroup size={size} isAttached variant="outline" borderColor="chalk.500">
      <Button
        onClick={() => onLangChange("hu")}
        aria-pressed={lang === "hu"}
        bg={lang === "hu" ? "ink.900" : "transparent"}
        color={lang === "hu" ? "chalk.50" : "ink.900"}
        _hover={{ bg: lang === "hu" ? "ink.800" : "chalk.200" }}
      >
        HU
      </Button>
      <Button
        onClick={() => onLangChange("en")}
        aria-pressed={lang === "en"}
        bg={lang === "en" ? "ink.900" : "transparent"}
        color={lang === "en" ? "chalk.50" : "ink.900"}
        _hover={{ bg: lang === "en" ? "ink.800" : "chalk.200" }}
      >
        EN
      </Button>
    </ButtonGroup>
  );

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={100}
      bg={scrolled ? "rgba(249, 250, 245, 0.85)" : "transparent"}
      backdropFilter={scrolled ? "blur(12px)" : "none"}
      borderBottom="1px solid"
      borderColor={scrolled ? "chalk.400" : "transparent"}
      boxShadow={scrolled ? "0 4px 20px rgba(20, 37, 26, 0.05)" : "none"}
      transition="background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease"
    >
      <Box width="100%" maxW="1400px" mx="auto" px={{ base: 5, md: 8 }} py={4}>
        <Flex justify="space-between" align="center">
          <Image
            w={{ base: 140, md: 170 }}
            src={logo}
            alt="Miller Productions logó"
            cursor="pointer"
            filter="brightness(0)"
            onClick={() => handleScroll("home")}
          />

          <Flex gap={3} align="center" display={{ base: "flex", md: "none" }}>
            {langToggle("xs")}
            <IconButton
              icon={<HamburgerIcon boxSize={6} />}
              aria-label={nav.menuLabel}
              onClick={onOpen}
              variant="outline"
              borderColor="chalk.500"
              color="ink.900"
              _hover={{ bg: "chalk.200", borderColor: "lime.600" }}
            />
          </Flex>

          <Flex gap={8} align="center" display={{ base: "none", md: "flex" }}>
            {nav.items.map((item) =>
              item.href ? (
                <Link
                  key={item.id}
                  as={RouterLink}
                  to={item.href}
                  fontSize="sm"
                  fontWeight="medium"
                  color="slate.700"
                  position="relative"
                  _hover={{ color: "ink.900", textDecoration: "none" }}
                >
                  {item.label}
                </Link>
              ) : (
                <Link
                  key={item.id}
                  onClick={() => handleScroll(item.id)}
                  fontSize="sm"
                  fontWeight="medium"
                  color="slate.700"
                  position="relative"
                  _hover={{ color: "ink.900", textDecoration: "none" }}
                >
                  {item.label}
                </Link>
              )
            )}
            {langToggle("sm")}
            <Button
              size="sm"
              bg="lime.500"
              color="ink.900"
              fontWeight="bold"
              _hover={{ bg: "lime.400" }}
              onClick={() => handleScroll("contacts")}
            >
              {nav.cta}
            </Button>
          </Flex>
        </Flex>
      </Box>

      <Drawer isOpen={isOpen} placement="top" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg="chalk.100" borderBottom="1px solid" borderColor="chalk.400">
          <DrawerCloseButton color="ink.900" _hover={{ color: "lime.600" }} />
          <DrawerBody bgColor="chalk.100" pb={10}>
            <Flex direction="column" gap={6} mt={12}>
              {nav.items.map((item) =>
                item.href ? (
                  <Link
                    key={item.id}
                    as={RouterLink}
                    to={item.href}
                    onClick={onClose}
                    fontSize="xl"
                    fontWeight="medium"
                    color="ink.900"
                    _hover={{ color: "lime.600", textDecoration: "none" }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <Link
                    key={item.id}
                    onClick={() => handleScroll(item.id)}
                    fontSize="xl"
                    fontWeight="medium"
                    color="ink.900"
                    _hover={{ color: "lime.600", textDecoration: "none" }}
                  >
                    {item.label}
                  </Link>
                )
              )}
              <Button
                bg="lime.500"
                color="ink.900"
                fontWeight="bold"
                _hover={{ bg: "lime.400" }}
                onClick={() => handleScroll("contacts")}
              >
                {nav.cta}
              </Button>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Header;
